import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Save } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import EntityCombobox from "@/components/EntityCombobox";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import ReceiptPrint, {
  type ReceiptPrintData,
} from "@/components/ReceiptPrint";
import { formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";

interface CompanySettings {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  currency_code?: string;
  currency_symbol?: string;
}

const PAYMENT_METHODS = [
  "Cash",
  "Mobile Money",
  "Bank Transfer",
  "Cheque",
  "Card",
];

interface QuoteOption {
  id: number;
  quote_number: string;
  customer_id: number | null;
  customer_name: string;
  total_amount?: number;
  total?: number;
  locked_by_waybill?: boolean;
  status?: string;
}

interface CustomerOption {
  id: number;
  name: string;
}

interface ReceiptApi {
  id: number;
  receipt_number: string;
  quote_id: number | null;
  quote_number: string | null;
  customer_id: number | null;
  customer_name: string;
  total_amount: number;
  amount_paid: number;
  balance: number;
  payment_method: string;
  payment_date: string | null;
  notes: string;
  locked_by_waybill: boolean;
  // True only for GENERAL payments whose customer has had on-account credit
  // consumed by a waybill. The form renders read-only and blocks save; the
  // receipt stays cancellable from the listing.
  locked_by_credit_application?: boolean;
  created_by_name?: string | null;
  payment_source?: "cash" | "credit_application";
  reference_quote_number?: string | null;
  reference_quote_amount?: number | null;
  cheque_number?: string | null;
  is_general_payment?: boolean;
}

interface CancellationApi {
  id: number;
  receipt_number: string;
  quote_number: string | null;
  customer_name: string;
  total_amount: number;
  amount_paid: number;
  balance: number;
  payment_method: string;
  payment_date: string | null;
  notes: string;
  reference_quote_number?: string | null;
  reference_quote_amount?: number | null;
  cheque_number?: string | null;
  cancellation_reason: string;
  cancelled_by: string;
  cancelled_at: string | null;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function draftReceiptNo() {
  return `RCT-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
}

export default function ReceiptForm() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { user, can } = useAuth();
  // Creating and editing are independently controllable now: receipts.create
  // gates the new-receipt flow, receipts.write gates editing an existing one.
  const canCreate = can("receipts.create");
  const canEdit = can("receipts.write");
  const isCancelledPreview = pathname.includes("/receipts/cancelled/");
  const isExisting = Boolean(id) && !isCancelledPreview;
  const isPreview =
    isCancelledPreview ||
    (Boolean(id) && !pathname.endsWith("/edit"));
  const editing = isExisting && pathname.endsWith("/edit");
  // The permission required to submit THIS form depends on the mode: editing
  // an existing receipt needs receipts.write, creating a new one needs
  // receipts.create.
  const canSubmit = editing ? canEdit : canCreate;
  const [lockedByWaybill, setLockedByWaybill] = useState(false);
  const [lockedByCreditApplication, setLockedByCreditApplication] =
    useState(false);
  const readOnly =
    isPreview || !canSubmit || lockedByWaybill || lockedByCreditApplication;
  const wantsAutoOpenPdf = useMemo(
    () => new URLSearchParams(search).get("openPdf") === "1",
    [search],
  );

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [quotes, setQuotes] = useState<QuoteOption[]>([]);
  const [paidByQuote, setPaidByQuote] = useState<Record<number, number>>({});
  const [editingQuoteOption, setEditingQuoteOption] =
    useState<QuoteOption | null>(null);

  const [receiptNumber, setReceiptNumber] = useState("");
  // General payment ("money on account") — no quotation attached. Builds the
  // customer's available credit, which a future waybill can draw on. NEW
  // receipts are always general payments; the edit-load path below still sets
  // this from the actual receipt so legacy quote-linked receipts view correctly.
  const [generalPayment, setGeneralPayment] = useState(true);
  // Reference-only quotation details for general payments. These are purely
  // for display / traceability — they do not allocate the payment, reduce
  // available credit, or mark any quotation paid.
  const [referenceQuoteNumber, setReferenceQuoteNumber] = useState("");
  const [referenceQuoteAmount, setReferenceQuoteAmount] = useState("");
  const [availableCredit, setAvailableCredit] = useState<number | null>(null);
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [quoteNumber, setQuoteNumber] = useState<string>("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [amountPaid, setAmountPaid] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  // Cheque number — required when the payment method is "Cheque" (applies to
  // both general and quote-linked receipts). Cleared when the method changes
  // away from Cheque. Reconciliation reference only; no accounting effect.
  const [chequeNumber, setChequeNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [notes, setNotes] = useState("");
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [amountFocused, setAmountFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelMeta, setCancelMeta] = useState<{
    reason: string;
    cancelledBy: string;
    cancelledAt: string;
  } | null>(null);

  const [company, setCompany] = useState<CompanySettings>({});
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfDocumentNumber, setPdfDocumentNumber] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [postSaveNavId, setPostSaveNavId] = useState<number | null>(null);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);

  // Best-effort load of company settings — used to brand the receipt PDF.
  // Falls back to the BillForge defaults if the user lacks settings.read.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
      } catch {
        // Non-fatal: PDF will still render with default branding.
      }
    })();
  }, []);

  useEffect(() => {
    // Pickers are populated from the Receipts-scoped form-support endpoint
    // so granting only the Receipts module is enough — admin does NOT need
    // to also tick Customers / Quotations / Receipts. The endpoint returns
    // customers + open quotations + amounts paid (so the outstanding
    // balance preview still works).
    (async () => {
      try {
        const res = await api.get<{
          customers: CustomerOption[];
          quotations: QuoteOption[];
          paid_by_quote: Record<string, number>;
        }>("/receipts/form-support");
        const allCustomers = (res.customers ?? []).slice().sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setCustomers(allCustomers);
        const allQuotes = res.quotations ?? [];
        const paidMapRaw = res.paid_by_quote ?? {};
        const paid: Record<number, number> = {};
        for (const [k, v] of Object.entries(paidMapRaw)) {
          const n = Number(k);
          if (Number.isFinite(n)) paid[n] = Number(v ?? 0);
        }
        setPaidByQuote(paid);
        // Only show quotations that are payable: not a draft (drafts can
        // never receive payment — the server enforces this too), not locked
        // by a waybill, and still carrying an outstanding balance.
        const unpaid = allQuotes.filter((q) => {
          if ((q.status ?? "").trim().toLowerCase() === "draft") return false;
          if (q.locked_by_waybill) return false;
          const total = Number(q.total_amount ?? q.total ?? 0);
          const p = Number(paid[q.id] ?? 0);
          return total - p > 0.01;
        });
        setQuotes(unpaid);
      } catch (err) {
        toast.error(
          (err as Error)?.message ||
            "Failed to load customer and quotation lists",
        );
      }
    })();
  }, []);

  // Whenever a customer is selected, surface their available credit ("money
  // on account"). Gated by receipts.read OR receipts.write on the API, so the
  // receipt form can always read it. Failures are non-fatal (hide the line).
  useEffect(() => {
    if (customerId == null) {
      setAvailableCredit(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          customer_id: number;
          available_credit: number;
        }>(`/receipts/customer-credit?customerId=${customerId}`);
        if (!cancelled) setAvailableCredit(Number(res.available_credit ?? 0));
      } catch {
        if (!cancelled) setAvailableCredit(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    if (!id) return;

    if (isCancelledPreview) {
      (async () => {
        try {
          const data = await api.get<{ cancellation: CancellationApi }>(
            `/receipts/cancellations/${id}`,
          );
          const c = data.cancellation;
          setReceiptNumber(c.receipt_number);
          setQuoteNumber(c.quote_number ?? "");
          setCustomerName(c.customer_name);
          setAmountPaid(Number(c.amount_paid));
          setPaymentMethod(c.payment_method || "Cash");
          setPaymentDate(
            c.payment_date
              ? new Date(c.payment_date).toISOString().slice(0, 10)
              : "",
          );
          setNotes(c.notes || "");
          setReferenceQuoteNumber(c.reference_quote_number ?? "");
          setReferenceQuoteAmount(
            c.reference_quote_amount != null
              ? String(c.reference_quote_amount)
              : "",
          );
          setChequeNumber(c.cheque_number ?? "");
          setEditingQuoteOption({
            id: 0,
            quote_number: c.quote_number ?? "",
            customer_id: null,
            customer_name: c.customer_name,
            total_amount: Number(c.total_amount),
          });
          setCancelMeta({
            reason: c.cancellation_reason,
            cancelledBy: c.cancelled_by,
            cancelledAt: c.cancelled_at
              ? new Date(c.cancelled_at).toLocaleString()
              : "",
          });
        } catch (err) {
          toast.error((err as Error).message || "Failed to load cancelled receipt");
        }
      })();
      return;
    }

    (async () => {
      try {
        const data = await api.get<{ receipt: ReceiptApi }>(`/receipts/${id}`);
        const r = data.receipt;
        setLockedByWaybill(!!r.locked_by_waybill);
        setLockedByCreditApplication(!!r.locked_by_credit_application);
        if (r.locked_by_waybill && pathname.endsWith("/edit")) {
          nav(`/receipts/${id}`, { replace: true });
          toast.error("This receipt is locked — preview only.");
        } else if (
          r.locked_by_credit_application &&
          pathname.endsWith("/edit")
        ) {
          nav(`/receipts/${id}`, { replace: true });
          toast.error(
            "This receipt is locked — its on-account credit has been applied to a waybill.",
          );
        }
        setReceiptNumber(r.receipt_number);
        setQuoteId(r.quote_id);
        setQuoteNumber(r.quote_number ?? "");
        setCustomerId(r.customer_id ?? null);
        setCustomerName(r.customer_name);
        setAmountPaid(Number(r.amount_paid));
        setPaymentMethod(r.payment_method || "Cash");
        setPaymentDate(
          r.payment_date
            ? new Date(r.payment_date).toISOString().slice(0, 10)
            : todayIso(),
        );
        setNotes(r.notes || "");
        setReferenceQuoteNumber(r.reference_quote_number ?? "");
        setReferenceQuoteAmount(
          r.reference_quote_amount != null
            ? String(r.reference_quote_amount)
            : "",
        );
        setChequeNumber(r.cheque_number ?? "");
        setCreatedByName(r.created_by_name ?? null);
        setGeneralPayment(!!r.is_general_payment || r.quote_id == null);
        if (r.quote_id) {
          setEditingQuoteOption({
            id: r.quote_id,
            quote_number: r.quote_number ?? "",
            customer_id: r.customer_id ?? null,
            customer_name: r.customer_name,
            total_amount: Number(r.total_amount),
          });
        }
      } catch (err) {
        toast.error((err as Error).message || "Failed to load receipt");
      }
    })();
  }, [id, isCancelledPreview, nav, pathname]);

  const effectiveQuotes = useMemo(() => {
    if (!editingQuoteOption) return quotes;
    if (quotes.some((q) => q.id === editingQuoteOption.id)) return quotes;
    return [editingQuoteOption, ...quotes];
  }, [quotes, editingQuoteOption]);

  const selectedQuote = useMemo(
    () => effectiveQuotes.find((q) => q.id === quoteId) ?? editingQuoteOption,
    [effectiveQuotes, quoteId, editingQuoteOption],
  );
  const totalAmount = Number(selectedQuote?.total_amount ?? selectedQuote?.total ?? 0);
  const paidElsewhere = Number(quoteId ? paidByQuote[quoteId] ?? 0 : 0);
  const outstandingBefore = Math.max(
    0,
    totalAmount -
      (editing
        ? paidElsewhere - Number(amountPaid)
        : paidElsewhere),
  );
  const balance = Math.max(0, outstandingBefore - Number(amountPaid || 0));

  // Show ALL customers in the dropdown so the user can always pick one — even
  // if they have no outstanding quotations yet. The Quotation Select below
  // will surface a clear "no outstanding quotations" message and a link to
  // create one. When editing/previewing an existing receipt we still want the
  // current customer to be selectable even if they were since deleted.
  const customerOptions = useMemo(() => {
    const list = customers.slice();
    if (customerId && !list.some((c) => c.id === customerId)) {
      list.unshift({ id: customerId, name: customerName || "(unknown)" });
    }
    return list;
  }, [customers, customerId, customerName]);

  const customerQuotes = useMemo(() => {
    if (customerId == null) return [];
    return effectiveQuotes.filter((q) => q.customer_id === customerId);
  }, [effectiveQuotes, customerId]);

  // Reference-quotation options for general payments: the loaded quotations
  // belonging to the selected customer. Reuses the same `quotes` list the
  // quotation picker is built from — purely for reference, never allocates.
  const referenceQuoteOptions = useMemo(() => {
    if (customerId == null) return [];
    return quotes.filter((q) => q.customer_id === customerId);
  }, [quotes, customerId]);

  function handleCustomerChange(value: string) {
    const idNum = Number(value);
    setCustomerId(idNum || null);
    const match = customers.find((c) => c.id === idNum);
    setCustomerName(match?.name ?? "");
    setQuoteId(null);
    setQuoteNumber("");
    // The previously chosen reference quotation may belong to a different
    // customer — reset the reference fields so they can't go stale.
    setReferenceQuoteNumber("");
    setReferenceQuoteAmount("");
  }

  // Selecting a reference quotation only fills the reference number/amount
  // fields. It does NOT set quote_id, allocate the payment, or change the
  // submission behaviour in any way.
  function handleReferenceQuoteSelect(idValue: string) {
    const idNum = Number(idValue);
    const q = referenceQuoteOptions.find((x) => x.id === idNum);
    if (!q) return;
    setReferenceQuoteNumber(q.quote_number ?? "");
    const amt = Number(q.total_amount ?? q.total ?? 0);
    setReferenceQuoteAmount(amt > 0 ? String(amt) : "");
  }

  // Changing the payment method away from Cheque clears the cheque number so
  // a stale value can't be submitted for a non-cheque payment.
  function handlePaymentMethodChange(value: string) {
    setPaymentMethod(value);
    if (value.trim().toLowerCase() !== "cheque") setChequeNumber("");
  }

  function handleQuoteChange(value: string) {
    const idNum = Number(value);
    const q = effectiveQuotes.find((x) => x.id === idNum);
    setQuoteId(idNum);
    setQuoteNumber(q?.quote_number ?? "");
    // Auto-fill the customer name from the selected quotation as a safety net.
    if (q?.customer_name) setCustomerName(q.customer_name);
  }

  function buildPrintDataFromForm(): ReceiptPrintData {
    return {
      number: receiptNumber || "Draft",
      paymentDate: paymentDate || null,
      customerName,
      quotationRef: quoteNumber || "",
      paymentMethod,
      totalAmount: totalAmount,
      amountPaid: Number(amountPaid || 0),
      balance: Math.max(0, totalAmount - Number(amountPaid || 0)),
      notes,
      referenceQuoteNumber: generalPayment
        ? referenceQuoteNumber.trim() || null
        : null,
      referenceQuoteAmount:
        generalPayment && Number(referenceQuoteAmount) > 0
          ? Number(referenceQuoteAmount)
          : null,
      chequeNumber:
        paymentMethod.trim().toLowerCase() === "cheque"
          ? chequeNumber.trim() || null
          : null,
      cancellation: isCancelledPreview && cancelMeta ? cancelMeta : null,
    };
  }

  function resolvePreparedBy(name?: string | null): string {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function generatePdf(printData: ReceiptPrintData, preparedBy: string) {
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
      currency: (company.currency_code || "GHS").toString(),
      currencySymbol: (company.currency_symbol || "GH\u20B5").toString(),
    };
    return renderReactToPdf(
      <ReceiptPrint
        receipt={printData}
        company={printCompany}
        preparedBy={preparedBy}
      />,
      {
        filename: safePdfFilename("Receipt", printData.number || "Draft"),
      },
    );
  }

  function openPdfPreview(
    pdf: { url: string; filename: string },
    documentNumber: string,
  ) {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(pdf.url);
    setPdfFilename(pdf.filename);
    setPdfDocumentNumber(documentNumber);
    setPdfModalOpen(true);
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
      setPdfDocumentNumber("");
      setPdfModalOpen(false);
      if (postSaveNavId != null) {
        nav(`/receipts/${postSaveNavId}`);
        setPostSaveNavId(null);
      }
    } else {
      setPdfModalOpen(true);
    }
  }

  async function handlePreviewPdfFromForm() {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const printData = buildPrintDataFromForm();
      const pdf = await generatePdf(
        printData,
        resolvePreparedBy(createdByName),
      );
      openPdfPreview(pdf, printData.number);
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  // Honour the ?openPdf=1 query param from the listing's "Preview PDF" row
  // action — once the receipt (active or cancelled) has finished loading,
  // auto-open the preview exactly once so re-renders don't loop the modal.
  useEffect(() => {
    if (!wantsAutoOpenPdf || autoOpenConsumed) return;
    if (!id) return;
    if (!receiptNumber) return;
    setAutoOpenConsumed(true);
    void handlePreviewPdfFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoOpenPdf, autoOpenConsumed, id, receiptNumber]);

  async function save() {
    if (readOnly || isPreview) return;
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }
    if (!generalPayment && !quoteId) {
      toast.error("Select a quotation");
      return;
    }
    if (!editing && amountPaid <= 0) {
      toast.error("Amount must be greater than zero");
      return;
    }
    if (!paymentMethod.trim()) {
      toast.error("Payment method is required");
      return;
    }
    // General payments have no outstanding cap — they build credit on
    // account. The outstanding guard only applies to quote-linked receipts.
    if (!generalPayment && !editing && amountPaid - outstandingBefore > 0.01) {
      toast.error("Amount paid cannot exceed outstanding balance");
      return;
    }
    // A note explaining the change is compulsory when editing an existing
    // receipt (it stays optional on create).
    if (editing && !notes.trim()) {
      toast.error("Please add a note explaining this edit.");
      return;
    }
    // Cheque payments require a cheque number (applies to general AND
    // quote-linked receipts). Mirrors the server-side validation.
    if (
      paymentMethod.trim().toLowerCase() === "cheque" &&
      !chequeNumber.trim()
    ) {
      toast.error("Cheque number is required when payment method is Cheque.");
      return;
    }
    setSaving(true);
    try {
      const chequeNumberPayload =
        paymentMethod.trim().toLowerCase() === "cheque"
          ? chequeNumber.trim()
          : null;
      const payload = {
        quote_id: quoteId,
        customer_id: customerId,
        customer_name: customerName,
        amount_paid: Number(amountPaid),
        payment_method: paymentMethod,
        payment_date: paymentDate
          ? new Date(paymentDate).toISOString()
          : null,
        notes,
        cheque_number: chequeNumberPayload,
      };
      let saved: ReceiptApi | null = null;
      if (editing) {
        const res = await api.put<{ receipt: ReceiptApi }>(
          `/receipts/${id}`,
          payload,
        );
        saved = res.receipt ?? null;
        toast.success("Receipt updated");
      } else if (generalPayment) {
        // Money on account — no quotation attached. The server credits the
        // customer's available balance for a future waybill to draw on.
        const res = await api.post<{ receipt: ReceiptApi }>("/receipts", {
          general_payment: true,
          customer_id: customerId,
          customer_name: customerName,
          amount_paid: Number(amountPaid),
          payment_method: paymentMethod,
          payment_date: paymentDate
            ? new Date(paymentDate).toISOString()
            : null,
          notes,
          reference_quote_number: referenceQuoteNumber.trim() || null,
          reference_quote_amount:
            Number(referenceQuoteAmount) > 0
              ? Number(referenceQuoteAmount)
              : null,
          cheque_number: chequeNumberPayload,
        });
        saved = res.receipt ?? null;
        toast.success("Payment recorded on account");
      } else {
        const res = await api.post<{ receipt: ReceiptApi }>(
          "/receipts",
          payload,
        );
        saved = res.receipt ?? null;
        toast.success("Receipt saved");
      }
      if (saved) {
        if (saved.receipt_number) setReceiptNumber(saved.receipt_number);
        if (saved.id != null) setPostSaveNavId(saved.id);
        setCreatedByName(saved.created_by_name ?? createdByName);
        const printData: ReceiptPrintData = {
          number: saved.receipt_number || receiptNumber || "Draft",
          paymentDate: saved.payment_date ?? paymentDate ?? null,
          customerName: saved.customer_name || customerName,
          quotationRef: saved.quote_number ?? quoteNumber,
          paymentMethod: saved.payment_method || paymentMethod,
          totalAmount: Number(saved.total_amount ?? totalAmount),
          amountPaid: Number(saved.amount_paid ?? amountPaid),
          balance: Number(saved.balance ?? 0),
          notes: saved.notes ?? notes,
          referenceQuoteNumber:
            saved.reference_quote_number ??
            (generalPayment ? referenceQuoteNumber.trim() || null : null),
          referenceQuoteAmount:
            saved.reference_quote_amount ??
            (generalPayment && Number(referenceQuoteAmount) > 0
              ? Number(referenceQuoteAmount)
              : null),
          chequeNumber:
            saved.cheque_number ??
            (paymentMethod.trim().toLowerCase() === "cheque"
              ? chequeNumber.trim() || null
              : null),
          cancellation: null,
        };
        try {
          const pdf = await generatePdf(
            printData,
            resolvePreparedBy(saved.created_by_name ?? createdByName),
          );
          openPdfPreview(pdf, printData.number);
        } catch (pdfErr) {
          toast.error((pdfErr as Error).message || "Could not render PDF");
          nav("/receipts");
        }
      } else {
        nav("/receipts");
      }
    } catch (err) {
      toast.error((err as Error).message || "Failed to save receipt");
    } finally {
      setSaving(false);
    }
  }

  const title = isCancelledPreview
    ? "View Cancelled Receipt"
    : isPreview
      ? "View Receipt"
      : editing
        ? "Edit Receipt"
        : "New Receipt";

  return (
    <div className="space-y-4 max-w-[1200px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/receipts")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {Boolean(id) ? (
            <Button
              type="button"
              variant="outline"
              className="h-9 px-3"
              onClick={handlePreviewPdfFromForm}
              disabled={generatingPdf}
            >
              <FileText className="h-4 w-4 mr-1" />
              {generatingPdf ? "Preparing..." : "Preview PDF"}
            </Button>
          ) : null}
        </div>
      </div>

      {isCancelledPreview && cancelMeta ? (
        <p className="text-sm text-rose-700 dark:text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
          Cancelled — {cancelMeta.reason}
          {cancelMeta.cancelledBy
            ? ` (by ${cancelMeta.cancelledBy}${cancelMeta.cancelledAt ? `, ${cancelMeta.cancelledAt}` : ""})`
            : ""}
        </p>
      ) : null}

      {lockedByWaybill && !isCancelledPreview ? (
        <p className="text-sm text-amber-700 dark:text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
          Locked — view only. A waybill exists for this quotation; this receipt
          cannot be changed or cancelled.
        </p>
      ) : null}

      {lockedByCreditApplication && !lockedByWaybill && !isCancelledPreview ? (
        <p className="text-sm text-amber-700 dark:text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
          Locked — view only. This on-account payment's credit has been applied
          to a waybill, so it can no longer be edited. You can still cancel it
          from the Receipts list.
        </p>
      ) : null}

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-base">Receipt Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>Receipt #</Label>
            <Input
              value={receiptNumber || draftReceiptNo()}
              readOnly
              className="h-10 font-mono"
            />
          </div>

          {generalPayment && (isPreview || readOnly) ? (
            <p className="text-sm text-muted-foreground border rounded-lg px-3 py-2">
              General payment — money on account (no quotation attached).
            </p>
          ) : null}

          {generalPayment && !readOnly ? (
            <div className="space-y-3 rounded-lg border border-dashed bg-muted/30 px-3 py-3">
              <div className="space-y-0.5">
                <Label className="text-sm">Reference quotation (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  For your records only. This does not allocate the payment to a
                  quotation, reduce available credit, or mark a quote as paid.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Reference quotation number
                  </Label>
                  <EntityCombobox
                    aria-label="Reference quotation number"
                    className="h-10"
                    value={referenceQuoteNumber}
                    disabled={!customerId}
                    placeholder={
                      customerId
                        ? referenceQuoteOptions.length
                          ? "Select quotation"
                          : "No quotations for this customer"
                        : "Select customer first"
                    }
                    options={referenceQuoteOptions.map((qq) => ({
                      id: qq.id,
                      name: qq.quote_number,
                      secondary: `Total ${formatNumber(
                        Number(qq.total_amount ?? qq.total ?? 0),
                      )}`,
                    }))}
                    emptyText="No quotations for this customer."
                    onSelect={(opt) =>
                      handleReferenceQuoteSelect(String(opt.id))
                    }
                    onClear={() => {
                      setReferenceQuoteNumber("");
                      setReferenceQuoteAmount("");
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Reference quotation amount (GHS)
                  </Label>
                  <Input
                    aria-label="Reference quotation amount"
                    type="number"
                    step="0.01"
                    value={referenceQuoteAmount}
                    onChange={(e) => setReferenceQuoteAmount(e.target.value)}
                    placeholder="Optional"
                    className="h-10"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {generalPayment &&
          (isPreview || readOnly) &&
          (referenceQuoteNumber.trim() || Number(referenceQuoteAmount) > 0) ? (
            <div className="grid grid-cols-2 gap-3 text-sm bg-accent rounded-lg p-3">
              <div>
                <div className="text-muted-foreground text-xs">
                  Reference quotation no.
                </div>
                <div className="font-mono font-semibold">
                  {referenceQuoteNumber.trim() || "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  Reference quotation amount
                </div>
                <div className="font-mono font-semibold">
                  {Number(referenceQuoteAmount) > 0
                    ? formatNumber(Number(referenceQuoteAmount))
                    : "—"}
                </div>
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">
                Reference only — not allocated to this quotation.
              </div>
            </div>
          ) : null}

          {readOnly ? (
            <div className="space-y-1.5">
              <Label>Customer Name</Label>
              <Input
                value={customerName}
                readOnly
                className="h-10"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Customer Name *</Label>
              <EntityCombobox
                aria-label="Customer name"
                className="h-10"
                value={customerName}
                disabled={editing || customerOptions.length === 0}
                placeholder={
                  customerOptions.length === 0
                    ? "No customers yet"
                    : "Select customer"
                }
                options={customerOptions.map((c) => ({
                  id: c.id,
                  name: c.name,
                }))}
                emptyText="No customers yet."
                onSelect={(opt) => handleCustomerChange(String(opt.id))}
                onClear={() => handleCustomerChange("")}
              />
              {customerOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No customers yet —{" "}
                  <Link
                    to="/customers"
                    className="text-primary hover:underline"
                  >
                    go to Customers to add one
                  </Link>
                  .
                </p>
              ) : null}
            </div>
          )}

          {customerId != null && availableCredit != null ? (
            <p className="text-xs text-muted-foreground">
              Available credit:{" "}
              <span className="font-medium text-foreground">
                GHS {formatNumber(availableCredit)}
              </span>
            </p>
          ) : null}

          {!generalPayment ? (
            readOnly ? (
            <div className="space-y-1.5">
              <Label>Quotation Number</Label>
              <Input
                value={quoteNumber || "—"}
                readOnly
                className="h-10 font-mono"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Quotation Number *</Label>
              <EntityCombobox
                aria-label="Quotation number"
                className="h-10"
                value={quoteNumber}
                disabled={!customerId || editing}
                placeholder={
                  customerId
                    ? customerQuotes.length
                      ? "Select quotation"
                      : "No quotations for this customer"
                    : "Select customer first"
                }
                options={customerQuotes.map((qq) => ({
                  id: qq.id,
                  name: qq.quote_number,
                  secondary: `Total ${formatNumber(
                    Number(qq.total_amount ?? qq.total ?? 0),
                  )}`,
                }))}
                emptyText="No outstanding quotations for this customer."
                onSelect={(opt) => handleQuoteChange(String(opt.id))}
                onClear={() => {
                  setQuoteId(null);
                  setQuoteNumber("");
                }}
              />
              {customerId && customerQuotes.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No outstanding quotations for this customer —{" "}
                  <Link
                    to="/quotations/new"
                    className="text-primary hover:underline"
                  >
                    create a quotation
                  </Link>
                  .
                </p>
              ) : null}
            </div>
            )
          ) : null}

          {!generalPayment && (selectedQuote || isCancelledPreview) ? (
            <div className="grid grid-cols-2 gap-3 text-sm bg-accent rounded-lg p-3">
              <div>
                <div className="text-muted-foreground text-xs">Total</div>
                <div className="font-mono font-semibold">
                  {formatNumber(totalAmount)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Outstanding</div>
                <div className="font-mono font-semibold">
                  {formatNumber(outstandingBefore)}
                </div>
              </div>
            </div>
          ) : null}

          <div className={generalPayment ? "" : "grid grid-cols-2 gap-3"}>
            <div className="space-y-1.5">
              <Label>Amount Paid (GHS) *</Label>
              <Input
                type="number"
                step="0.01"
                value={
                  readOnly
                    ? amountPaid
                    : amountFocused && amountPaid === 0
                      ? ""
                      : amountPaid
                }
                onFocus={() => setAmountFocused(true)}
                onBlur={() => setAmountFocused(false)}
                onChange={(e) => setAmountPaid(Number(e.target.value || 0))}
                readOnly={readOnly || editing}
                className="h-10"
              />
            </div>
            {!generalPayment ? (
              <div className="space-y-1.5">
                <Label>Balance (GHS)</Label>
                <Input
                  value={balance.toFixed(2)}
                  readOnly
                  className="h-10"
                />
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethod || undefined}
                onValueChange={clearable(handlePaymentMethodChange)}
                disabled={readOnly || editing}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {PAYMENT_METHODS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                readOnly={readOnly || editing}
                className="h-10"
              />
            </div>
          </div>

          {paymentMethod.trim().toLowerCase() === "cheque" ? (
            <div className="space-y-1.5">
              <Label>Cheque Number *</Label>
              <Input
                aria-label="Cheque number"
                value={chequeNumber}
                onChange={(e) => setChequeNumber(e.target.value)}
                readOnly={readOnly}
                placeholder="Enter cheque number"
                className="h-10 font-mono"
              />
              {!readOnly ? (
                <p className="text-xs text-muted-foreground">
                  Required when the payment method is Cheque.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label>{editing ? "Notes *" : "Notes"}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              readOnly={readOnly}
              placeholder={
                editing ? "Explain why this receipt is being edited..." : undefined
              }
            />
            {editing ? (
              <p className="text-xs text-muted-foreground">
                A note is required when editing a receipt.
              </p>
            ) : null}
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => nav("/receipts")}
              className="min-w-24"
            >
              {isPreview ? "Back" : "Cancel"}
            </Button>
            {!isPreview && canSubmit ? (
              <Button
                className="bg-violet-500 hover:bg-violet-400 text-white min-w-32"
                onClick={save}
                disabled={saving || lockedByWaybill || lockedByCreditApplication}
              >
                <Save className="h-4 w-4 mr-2" />
                {saving
                  ? "Saving..."
                  : editing
                    ? "Update Receipt"
                    : "Save Receipt"}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Receipt PDF"
        documentNumber={pdfDocumentNumber}
      />
    </div>
  );
}
